import { useMemo, useCallback, useEffect } from 'react';

import { useAtomValue } from 'jotai';
import * as THREE from 'three';

import { activeData3dAtom, comparisonData3dAtom } from '@store';

import { Camera } from './Camera/Camera';
import { useElvaVisualizerContext } from './ElvaVisualizerContext';
import { parseFocusId, getKeypointId, getSafeSkeleton } from './helpers';
import { IntrinsicCamera } from './IntrinsicCamera';

import type { ElvaVisualizerProps } from './types';

export const FOV = 54;
const MAX_CAMERA_DISTANCE = 4;
const MIN_CAMERA_DISTANCE = 1.1;

export const CameraControls = ({
    fallbackFocus,
    fallbackFocusFrame = 0,
    onCameraInteraction,
    verticalOffset = 0,
    horizontalOffset = 0,
    comparisonFrame,
}: ElvaVisualizerProps) => {
    const activeData = useAtomValue(activeData3dAtom)!;
    const comparisonData = useAtomValue(comparisonData3dAtom);

    const {
        currentFocusId,
        intrinsicCameraParams,
        cameraMode,
        forcedAzimuthalAngle,
        forcedPolarAngle,
        setForcedAzimuthalAngle,
        setForcedPolarAngle,
        setCurrentFocusId,
        setCameraMode,
        setIntrinsicCameraParams,
    } = useElvaVisualizerContext();
    const currentFocus = useMemo(() => parseFocusId(currentFocusId), [currentFocusId]);

    // Determine if we have an active focus target (keypoint to track)
    const hasFocusTarget = !!(currentFocus?.keypointName ?? fallbackFocus);

    // Default center for pan bounds (target line at waist height)
    const panBoundsCenter = useMemo(() => {
        if (comparisonData && typeof comparisonFrame === 'number') {
            return new THREE.Vector3(-0.5, 1, 0);
        }
        return new THREE.Vector3(0, 1, 0);
    }, [comparisonData, comparisonFrame]);

    const cameraFocusPoint = useMemo(() => {
        const keypointName = currentFocus?.keypointName ?? fallbackFocus;

        // If no keypoint specified, no focus target (free pan mode)
        if (!keypointName) {
            return undefined;
        }

        const keypoint = getKeypointId(keypointName, activeData.keypointIdToName);
        const vec = getSafeSkeleton(activeData.skeleton, fallbackFocusFrame, keypoint)!;
        const primaryFocus = new THREE.Vector3(vec[1], vec[2], vec[0]);

        // If comparison data exists, average the focus points between primary and comparison skeletons
        if (comparisonData && typeof comparisonFrame === 'number') {
            try {
                const comparisonKeypoint = getKeypointId(keypointName, comparisonData.keypointIdToName);
                // Use fallbackFocusFrame for comparison too, so camera stays fixed when a preset is active
                const comparisonFocusFrame = fallbackFocusFrame;
                const comparisonVec = getSafeSkeleton(
                    comparisonData.skeleton,
                    comparisonFocusFrame,
                    comparisonKeypoint,
                )!;
                // Comparison skeleton is offset by [0, -1, 0] in local coords, which is [0, 0, -1] after rotation transform
                const comparisonFocus = new THREE.Vector3(comparisonVec[1] - 1, comparisonVec[2], comparisonVec[0]);
                // Average the two focus points
                return primaryFocus.add(comparisonFocus).multiplyScalar(0.5);
            } catch {
                // If comparison keypoint lookup fails, fall back to primary only
                return primaryFocus;
            }
        }

        return primaryFocus;
    }, [
        activeData.skeleton,
        activeData.keypointIdToName,
        currentFocus,
        fallbackFocus,
        fallbackFocusFrame,
        comparisonData,
        comparisonFrame,
    ]);

    const focusReset = useCallback(() => {
        setForcedAzimuthalAngle(null);
        setForcedPolarAngle(null);
        setCurrentFocusId(null);
        setCameraMode('orbit');
        setIntrinsicCameraParams(null);
    }, [setCurrentFocusId, setForcedAzimuthalAngle, setForcedPolarAngle, setCameraMode, setIntrinsicCameraParams]);

    // This effect handles resetting focus visuals when incoming selected params change
    useEffect(() => {
        focusReset();
    }, [focusReset]);

    return cameraMode === 'intrinsic' && intrinsicCameraParams ? (
        <IntrinsicCamera
            params={intrinsicCameraParams}
            minDistance={MIN_CAMERA_DISTANCE}
            maxDistance={MAX_CAMERA_DISTANCE}
        />
    ) : (
        <Camera
            target={cameraFocusPoint}
            forcedAzimuthalAngle={forcedAzimuthalAngle}
            forcedPolarAngle={forcedPolarAngle}
            minDistance={MIN_CAMERA_DISTANCE}
            maxDistance={MAX_CAMERA_DISTANCE}
            verticalOffset={verticalOffset}
            horizontalOffset={horizontalOffset}
            enablePan={!hasFocusTarget}
            panBoundsCenter={panBoundsCenter}
            onRotate={onCameraInteraction}
        />
    );
};
